// src/App.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import './App.css'; // <-- Importar el archivo CSS

const maxExportSize = parseInt(process.env.ELASTICSEARCH_EXPORT_LIMIT=100000 || '10000', 10); // Lee del .env

// --- Componente Modal usando Clases CSS ---
const ExportModal = () => {
  const { t } = useTranslation();
  return (
    <div className="modal-overlay"> {/* Usa clase CSS */}
      <div className="modal-content"> {/* Usa clase CSS */}
        <div className="modal-spinner"></div> {/* Usa clase CSS */}
        <h4>{t('exportModalTitle')}</h4>
        <p>{t('exportModalBody')}</p>
        <p style={{fontSize: '0.85em', color: '#6c757d', marginTop: '15px'}}>
                {t('exportModalLimitInfo', { maxExportSize: maxExportSize.toLocaleString() })}
        </p>
      </div>
    </div>
  );
};
// ------------------------------------------


function App() {
  // --- Hook de Traducción y Matomo ---
  const { t, i18n } = useTranslation();
  // Asegúrate de obtener todas las funciones que necesitas de Matomo
  const { trackPageView, trackEvent, trackSiteSearch, pushInstruction } = useMatomo();

  // --- Estados ---
  const [user, setUser] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [yearFilter, setYearFilter] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [activeView, setActiveView] = useState('leaks');
  const [domainData, setDomainData] = useState([]);
  const [isDomainsLoading, setIsDomainsLoading] = useState(false);
  const [isDomainSearching, setIsDomainSearching] = useState(false);
  const [domainError, setDomainError] = useState(null);
  const [domainSearchTerm, setDomainSearchTerm] = useState('');
  const [domainCurrentPage, setDomainCurrentPage] = useState(1);
  const [domainPageSize, setDomainPageSize] = useState(10);
  const [domainTotalItems, setDomainTotalItems] = useState(0);
  const [domainTotalPages, setDomainTotalPages] = useState(0);
  // El estado showPasswords fue eliminado

  // --- Configuración API ---
  const backendUrl = process.env.REACT_APP_BACKEND_URL || ''; // Obtener URL base backend
  const domainsEnabled = (process.env.REACT_APP_ENABLE_DOMAINS || 'false').toLowerCase() === 'true';
  const apiUrl = `${backendUrl}/api`; // Construir URL de API
  const loginUrl = `${backendUrl}/login`; // Construir URL de Login
  const logoutBackendUrl = `${backendUrl}/logout`; // <-- CONSTRUIR URL DE LOGOUT BACKEND

  // --- Efecto para verificar usuario Y SETEAR USER ID ---
  useEffect(() => {
    const checkUser = async () => {
      try {
        const response = await axios.get(`${apiUrl}/user`, { withCredentials: true });
        const userData = response.data;
        setUser(userData);
        // --- Establecer User ID en Matomo ---
        if (userData && userData.sub) {
            console.log("Matomo setUserId:", userData.sub);
            pushInstruction('setUserId', userData.sub); // <-- LLAMADA A setUserId
        } else {
             console.log("Matomo resetUserId: No user data or sub found");
             pushInstruction('resetUserId'); // <-- LLAMADA A resetUserId
        }
        // --------------------------------------
      } catch (err) {
        if (err.response && err.response.status === 401) {
          console.log("Usuario no autenticado.");
          setUser(null);
          pushInstruction('resetUserId'); // <-- LLAMADA A resetUserId
        } else {
          console.error("Error verificando usuario:", err);
          setError(t('errorVerifyUser'));
          pushInstruction('resetUserId'); // <-- LLAMADA A resetUserId
        }
      }
    };
    checkUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, t, pushInstruction]); // <-- Asegúrate que estén en las dependencias

  // --- Efecto para Trackear Page View inicial ---
  useEffect(() => {
    console.log("Matomo trackPageView called (initial mount)");
    trackPageView();
  }, [trackPageView]);

  // --- Handlers Login/Logout ---
  const handleLogin = () => { window.location.href = loginUrl; };

  const handleLogout = () => {
    console.log("Matomo resetUserId on logout");
    pushInstruction('resetUserId'); // <-- LLAMADA A resetUserId
    setUser(null); setData([]); setSearchTerm(''); setCurrentPage(1);
    setTotalItems(0); setTotalPages(0); setError(null);
    if (!logoutBackendUrl) { console.error("Logout URL no está configurada!"); return; }
    window.location.href = logoutBackendUrl;
  };

  const handleViewChange = (view) => {
    if (view === 'domains' && !domainsEnabled) {
      return;
    }
    setActiveView(view);
    setError(null);
    setDomainError(null);
  };

   // --- Handler Cambio Tamaño Página ---
  const handlePageSizeChange = (event) => {
      const newSize = parseInt(event.target.value, 10);
      if (!isNaN(newSize) && newSize > 0) {
          setPageSize(newSize);
          setCurrentPage(1);
          fetchData(1, newSize, searchTerm, yearFilter);
      }
  };

    const handleYearChange = (event) => {
      const newYear = event.target.value;
        setYearFilter(newYear);
        setCurrentPage(1);
        fetchData(1, pageSize, searchTerm, newYear);
    };

  // --- fetchData ---
  const fetchData = async (page = currentPage, size = pageSize, term = searchTerm, year = undefined) => {
    if (!user) { setError(t('errorLoginNeeded')); return; }
    const isActualSearch = term && term !== '';
    const isNewSearchOrDifferentTerm = term !== searchTerm;
    if (isNewSearchOrDifferentTerm && isActualSearch) { setIsSearching(true); }
    else { setIsLoading(true); }
    setError(null);
    try {
      const yearToUse = year !== undefined ? year : yearFilter;
      const searchParams = new URLSearchParams({ q: term, page: page, pageSize: size });
      if (yearToUse && yearToUse !== '') { searchParams.append('year', yearToUse); }
      const url = `${apiUrl}/data?${searchParams.toString()}`;
      console.log("Fetching data from:", url);
      const response = await axios.get(url, { withCredentials: true });
      const responseData = response.data.data || [];
      const paginationData = response.data.pagination || {};
      const currentTotalItems = paginationData.total || 0;
      setData(responseData); setTotalItems(currentTotalItems);
      setCurrentPage(paginationData.page || 1); setPageSize(paginationData.pageSize || 10);
      setTotalPages(paginationData.totalPages || 0);
      // --- Track Site Search ---
      if (isActualSearch) {
          console.log(`Matomo trackSiteSearch: Keyword='${term}', Count=${currentTotalItems}`);
          trackSiteSearch({ keyword: term, count: currentTotalItems });
      }
      // -------------------------
    } catch (err) {
      console.error("Error fetching data:", err); // Mantener log para depuración
  
      let errorMsg = t('errorLoadingData'); // Mensaje de error por defecto
      let resetDataOnError = true; // Por defecto, asumimos que borraremos los datos en caso de error
  
      if (err.response) {
        // El servidor respondió con un código de estado fuera del rango 2xx
        const status = err.response.status;
        const responseData = err.response.data; // Puede ser JSON o Blob
  
        if (status === 401) {
            // Error de Autenticación
            errorMsg = t('errorSessionExpired');
            setUser(null); // Desloguear usuario en el frontend
            pushInstruction('resetUserId'); // Limpiar User ID de Matomo
            // Resetear datos sí es apropiado aquí
        } else if (status === 429) {
            // --- ERROR DE RATE LIMIT ---
            let description = err.message; // Mensaje fallback
            // Intentar obtener descripción más detallada si el backend la envía en JSON
            if (responseData && typeof responseData === 'object' && responseData.description) {
                 description = responseData.description;
            } else if (responseData && responseData instanceof Blob){
                 // Intentar leer JSON del blob (si backend envía JSON en error 429)
                 try {
                    const errorJson = JSON.parse(await responseData.text());
                    if (errorJson.description) description = errorJson.description;
                 } catch(parseErr) { /* Ignorar error de parseo */ }
            }
            errorMsg = t('errorRateLimit', { description });
            // !!! IMPORTANTE: NO resetear datos para el error 429 !!!
            resetDataOnError = false;
            // ---------------------------
        } else if (responseData && typeof responseData === 'object' && responseData.error) {
             // Otros errores 5xx o 4xx del backend que devuelven JSON
             errorMsg = `${t('errorFromServerPrefix')}${responseData.error}${responseData.details ? ' - ' + responseData.details : ''}`;
             // Resetear datos
        } else {
             // Otros errores HTTP sin cuerpo JSON específico
             errorMsg = `${t('errorFromServerPrefix')}HTTP ${status}`;
             // Resetear datos
        }
      } else if (err.request) {
        // La petición se hizo pero no se recibió respuesta (red, timeout del cliente)
        errorMsg = t('errorNetwork');
        // Resetear datos
      } else {
        // Error al configurar/enviar la petición
        errorMsg = t('errorRequestSetup');
        // Resetear datos
      }
  
      // Establecer el mensaje de error final en el estado
      setError(errorMsg);
  
      // Resetear los datos de la tabla y paginación SOLO si no fue un error 429
      if (resetDataOnError) {
          console.log("Reseteando datos debido a error (NO 429)");
          setData([]);
          setTotalItems(0);
          setTotalPages(0);
          // Considera si quieres resetear currentPage a 1 aquí también
          // setCurrentPage(1);
      }
  
    } finally {
       // Quitar siempre los indicadores de carga
       setIsLoading(false);
       setIsSearching(false);
    }
     
 
 
 
    };

  const fetchDomainData = async (page = domainCurrentPage, size = domainPageSize, term = domainSearchTerm) => {
    if (!user) { setDomainError(t('errorLoginNeeded')); return; }
    const isActualSearch = term && term !== '';
    const isNewSearchOrDifferentTerm = term !== domainSearchTerm;
    if (isNewSearchOrDifferentTerm && isActualSearch) { setIsDomainSearching(true); }
    else { setIsDomainsLoading(true); }
    setDomainError(null);
    try {
      const searchParams = new URLSearchParams({ q: term, page: page, pageSize: size });
      const url = `${apiUrl}/domains?${searchParams.toString()}`;
      const response = await axios.get(url, { withCredentials: true });
      const responseData = response.data.data || [];
      const paginationData = response.data.pagination || {};
      const currentTotalItems = paginationData.total || 0;
      setDomainData(responseData);
      setDomainTotalItems(currentTotalItems);
      setDomainCurrentPage(paginationData.page || 1);
      setDomainPageSize(paginationData.pageSize || 10);
      setDomainTotalPages(paginationData.totalPages || 0);
      if (isActualSearch) {
        trackSiteSearch({ keyword: term, count: currentTotalItems, category: 'domains' });
      }
    } catch (err) {
      let errorMsg = t('errorLoadingData');
      if (err.response) {
        const status = err.response.status;
        const responseData = err.response.data;
        if (status === 401) {
          errorMsg = t('errorSessionExpired');
          setUser(null);
          pushInstruction('resetUserId');
        } else if (status === 429) {
          let description = err.message;
          if (responseData && typeof responseData === 'object' && responseData.description) {
            description = responseData.description;
          }
          errorMsg = t('errorRateLimit', { description });
        } else if (responseData && typeof responseData === 'object' && responseData.error) {
          errorMsg = `${t('errorFromServerPrefix')}${responseData.error}${responseData.details ? ' - ' + responseData.details : ''}`;
        } else {
          errorMsg = `${t('errorFromServerPrefix')}HTTP ${status}`;
        }
      } else if (err.request) {
        errorMsg = t('errorNetwork');
      } else {
        errorMsg = t('errorRequestSetup');
      }
      setDomainError(errorMsg);
      setDomainData([]);
      setDomainTotalItems(0);
      setDomainTotalPages(0);
    } finally {
      setIsDomainsLoading(false);
      setIsDomainSearching(false);
    }
  };

  // --- Handlers Búsqueda ---
  const handleSearchChange = (event) => { setSearchTerm(event.target.value); };
  const handleSearchSubmit = (event) => { event.preventDefault(); setCurrentPage(1); fetchData(1, pageSize, searchTerm); };
  const handleDomainSearchChange = (event) => { setDomainSearchTerm(event.target.value); };
  const handleDomainSearchSubmit = (event) => { event.preventDefault(); setDomainCurrentPage(1); fetchDomainData(1, domainPageSize, domainSearchTerm); };

  // --- Handlers Paginación ---
  const handlePreviousPage = () => { if (currentPage > 1) { fetchData(currentPage - 1, pageSize, searchTerm, yearFilter); } };
  const handleNextPage = () => { if (currentPage < totalPages) { fetchData(currentPage + 1, pageSize, searchTerm, yearFilter); } };
  const handleDomainPreviousPage = () => { if (domainCurrentPage > 1) { fetchDomainData(domainCurrentPage - 1, domainPageSize, domainSearchTerm); } };
  const handleDomainNextPage = () => { if (domainCurrentPage < domainTotalPages) { fetchDomainData(domainCurrentPage + 1, domainPageSize, domainSearchTerm); } };
  const handleDomainPageSizeChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    if (!isNaN(newSize) && newSize > 0) {
      setDomainPageSize(newSize);
      setDomainCurrentPage(1);
      fetchDomainData(1, newSize, domainSearchTerm);
    }
  };

  // --- Handler Exportar Excel ---
  const handleExportExcel = async () => {
    if (isExporting) return;
    setIsExporting(true); setError(null);
    trackEvent({ category: 'Export', action: 'ClickExportAll', name: searchTerm || 'empty' });
    const searchParams = new URLSearchParams({ q: searchTerm });
    if (yearFilter && yearFilter !== '') { searchParams.append('year', yearFilter); }
    const exportUrl = `${apiUrl}/data/export?${searchParams.toString()}`;
    console.log("Solicitando exportación de Excel desde:", exportUrl);
    try {
        const response = await axios.get(exportUrl, { withCredentials: true, responseType: 'blob', timeout: 300000 });
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        let filename = `exportacion_total_${searchTerm || 'todos'}.xlsx`;
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
            if (filenameMatch && filenameMatch.length === 2) { filename = filenameMatch[1]; }
        }
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url; link.setAttribute('download', filename);
        document.body.appendChild(link); link.click();
        link.parentNode.removeChild(link); window.URL.revokeObjectURL(url);
    } catch (err) {
         console.error("Error durante la exportación:", err);
         let errorMsg = t('errorExportGeneric');
         if (err.response && err.response.data && err.response.data instanceof Blob) {
             try {
                 const errorJson = JSON.parse(await err.response.data.text());
                 if (errorJson.error) { errorMsg = `${t('errorFromServerPrefix')}${errorJson.error}${errorJson.details ? ' - ' + errorJson.details : ''}`; }
             } catch (parseError) { console.error("No se pudo parsear el blob de error:", parseError); }
         } else if (axios.isCancel(err)) { errorMsg = t('errorExportCancelled'); }
         else if (err.code === 'ECONNABORTED') { errorMsg = t('errorExportTimeout'); }
         setError(errorMsg);
    } finally { setIsExporting(false); }
  };

  // --- Formateador de Timestamps ---
  const formatTimestamp = (ts) => {
    if (ts === undefined || ts === null) return '';
    // Aceptar números o strings numéricos (segundos o milisegundos), o ISO strings
    let value = ts;
    if (typeof value === 'string' && /^\d+$/.test(value)) {
      value = Number(value);
    }
    if (typeof value === 'number') {
      // Si parece estar en segundos (10 dígitos), convertir a ms
      if (value > 0 && value < 1e11) { value = value * 1000; }
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(ts);
    return date.toLocaleString();
  };

  // --- Helper para mostrar campos con múltiples posibles claves y fallback ---
  const displayOrFallback = (obj, keys) => {
    if (!obj) return '-';
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    return '-';
  };
  // Helper: derive the year of an item from known timestamp fields
  const getItemYear = (item) => {
    if (!item) return null;
    const candidates = [item.timestamp, item.created_at, item.date, item.time];
    for (const c of candidates) {
      if (c === undefined || c === null) continue;
      let v = c;
      if (typeof v === 'string' && /^\d+$/.test(v)) v = Number(v);
      if (typeof v === 'number') {
        if (v > 0 && v < 1e11) v = v * 1000; // seconds -> ms
      }
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.getFullYear();
    }
    return null;
  };

  // Apply year filter client-side to currently loaded data so changing the year
  // immediately updates visible rows without waiting for a server search.
  const visibleData = (yearFilter && yearFilter !== '')
    ? data.filter((item) => {
        const y = getItemYear(item);
        return y !== null && String(y) === String(yearFilter);
      })
    : data;

  const displayedTotalItems = (yearFilter && yearFilter !== '') ? visibleData.length : totalItems;
  const isDomainsViewActive = domainsEnabled && activeView === 'domains';
   // --- Renderizado del Componente usando Clases CSS ---
   return (
     // Contenedor principal con clase CSS
     <div className="App">
       {/* Cabecera con clase CSS */}
       <header className="App-header">
      <h1>{t('appTitle')}</h1>

     {/* Agrupar elementos de la derecha */}
     <div className="header-right-controls">
       <div className="language-switcher">
          <button onClick={() => {i18n.changeLanguage('en'); /* trackEvent(...) */}} disabled={i18n.language === 'en'}>EN</button>
          <button onClick={() => {i18n.changeLanguage('es'); /* trackEvent(...) */}} disabled={i18n.language === 'es'}>ES</button>
       </div>

       {user ? (
         // Div específico para usuario y logout
         <div className="user-actions">
           <span>{t('welcomeMessage', { user: user.name || user.preferred_username || user.sub })}</span>
           <button onClick={handleLogout}>{t('logoutButton')}</button>
         </div>
       ) : (
         // Div para el botón de login si quieres mantener la estructura
         <div className="login-action">
           <p>{t('loginPrompt')}</p>
           <button onClick={handleLogin}>{t('loginButton')}</button>
         </div>
       )}
     </div>
      </header>
      <main>
        {user && (
          <div className="view-switcher">
            <button className={activeView === 'leaks' ? 'active' : ''} onClick={() => handleViewChange('leaks')}>{t('leaksViewButton')}</button>
            {domainsEnabled && (
              <button className={activeView === 'domains' ? 'active' : ''} onClick={() => handleViewChange('domains')}>{t('domainsViewButton')}</button>
            )}
          </div>
        )}

        {!isDomainsViewActive ? (
          <>
            {user && (
              <form onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  aria-label={t('searchPlaceholder')}
                />
                <button type="submit" disabled={isSearching || isLoading}>
                  {isSearching ? t('searchButtonLoading') : t('searchButton')}
                </button>
              </form>
            )}

            {error && <p className="error-message">{t('errorPrefix')}: {error}</p>}
            {isLoading && <p className="loading-message">{t('loadingTableData')}</p>}

            {user && !isLoading && ( <>
                {totalItems > 0 && ( <div>
                    <div className="controls-bar">
                      <span>{t('resultsInfo', { totalItems: displayedTotalItems, currentPage, totalPages })}</span>
                      {totalPages > 1 && (
                       <div>
                          <button onClick={handlePreviousPage} disabled={currentPage <= 1 || isLoading}>{t('previousButton')}</button>
                          <button onClick={handleNextPage} disabled={currentPage >= totalPages || isLoading}>{t('nextButton')}</button>
                       </div>
                      )}
                      <div>
                         <label htmlFor="pageSizeSelect">{t('itemsPerPageLabel')}</label>
                         <select id="pageSizeSelect" value={pageSize} onChange={handlePageSizeChange} disabled={isLoading}>
                           <option value="10">10</option> <option value="25">25</option> <option value="50">50</option> <option value="100">100</option>
                         </select>
                         <label htmlFor="yearSelect" style={{ marginLeft: '12px' }}>{t('yearFilterLabel')}</label>
                         <select id="yearSelect" value={yearFilter} onChange={handleYearChange} disabled={isLoading} style={{ marginLeft: '6px' }}>
                           <option value="">{t('yearFilterAll')}</option>
                           <option value="2026">2026</option>
                           <option value="2025">2025</option>
                         </select>
                       <button onClick={handleExportExcel} disabled={visibleData.length === 0 || isExporting} className="export-button">
                          {isExporting ? t('exportButtonLoading') : t('exportButton')}
                        </button>
                      </div>
                    </div>

                    <div className="table-container">
                       <table>
                         <thead> <tr>
                             <th>{t('tableHeaderSite')}</th>
                             <th>{t('tableHeaderUsername')}</th>
                             <th>{t('tableHeaderPassword')}</th>
                             <th>{t('tableHeaderDomain')}</th>
                             <th>{t('tableHeaderPublicSuffix')}</th>
                             <th>{t('tableHeaderCountryCode')}</th>
                             <th>{t('tableHeaderTimestamp')}</th>
                           </tr> </thead>
                             <tbody> {visibleData.map((item, index) => ( <tr key={item.id || index}>
                               <td>{item.site}</td>
                               <td>{item.username}</td>
                               <td>{item.password}</td>
                               <td>{displayOrFallback(item, ['domain', 'domain_name', 'host'])}</td>
                               <td>{displayOrFallback(item, ['public_suffix', 'publicSuffix', 'suffix'])}</td>
                               <td>{displayOrFallback(item, ['country_code', 'country', 'countryCode'])}</td>
                               <td>{formatTimestamp(item.timestamp || item.created_at || item.date || item.time) || '-'}</td>
                             </tr> ))} </tbody>
                       </table>
                    </div>

                     {totalPages > 1 && ( <div className="pagination-controls-bottom" style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                          <button onClick={handlePreviousPage} disabled={currentPage <= 1 || isLoading}>{t('previousButton')}</button>
                          <span>{t('pageInfo', { currentPage, totalPages })}</span>
                          <button onClick={handleNextPage} disabled={currentPage >= totalPages || isLoading}>{t('nextButton')}</button>
                        </div> )}
                  </div>
                )}
                {!isLoading && totalItems === 0 && searchTerm && ( <p className="info-message">{t('noResultsFound', { searchTerm })}</p> )}
                {!isLoading && totalItems === 0 && !searchTerm && !error && ( <p className="info-message">{t('noDataDefault')}</p> )}
              </>
            )}
          </>
        ) : (
          <>
            {user && (
              <form onSubmit={handleDomainSearchSubmit}>
                <input
                  type="text"
                  placeholder={t('domainsSearchPlaceholder')}
                  value={domainSearchTerm}
                  onChange={handleDomainSearchChange}
                  aria-label={t('domainsSearchPlaceholder')}
                />
                <button type="submit" disabled={isDomainSearching || isDomainsLoading}>
                  {isDomainSearching ? t('searchButtonLoading') : t('searchButton')}
                </button>
              </form>
            )}

            {domainError && <p className="error-message">{t('errorPrefix')}: {domainError}</p>}
            {isDomainsLoading && <p className="loading-message">{t('loadingTableData')}</p>}

            {user && !isDomainsLoading && ( <>
                {domainTotalItems > 0 && ( <div>
                    <div className="controls-bar">
                      <span>{t('resultsInfo', { totalItems: domainTotalItems, currentPage: domainCurrentPage, totalPages: domainTotalPages })}</span>
                      {domainTotalPages > 1 && (
                       <div>
                          <button onClick={handleDomainPreviousPage} disabled={domainCurrentPage <= 1 || isDomainsLoading}>{t('previousButton')}</button>
                          <button onClick={handleDomainNextPage} disabled={domainCurrentPage >= domainTotalPages || isDomainsLoading}>{t('nextButton')}</button>
                       </div>
                      )}
                      <div>
                         <label htmlFor="domainPageSizeSelect">{t('itemsPerPageLabel')}</label>
                         <select id="domainPageSizeSelect" value={domainPageSize} onChange={handleDomainPageSizeChange} disabled={isDomainsLoading}>
                           <option value="10">10</option> <option value="25">25</option> <option value="50">50</option> <option value="100">100</option>
                         </select>
                      </div>
                    </div>

                    <div className="table-container">
                       <table>
                         <thead> <tr>
                             <th>{t('tableHeaderDomain')}</th>
                             <th>{t('tableHeaderPublicSuffix')}</th>
                             <th>{t('tableHeaderCountryCode')}</th>
                             <th>{t('tableHeaderTimestamp')}</th>
                           </tr> </thead>
                             <tbody> {domainData.map((item, index) => ( <tr key={item.id || item.domain || item.domain_name || item.host || index}>
                               <td>{displayOrFallback(item, ['domain', 'domain_name', 'host'])}</td>
                               <td>{displayOrFallback(item, ['public_suffix', 'publicSuffix', 'suffix'])}</td>
                               <td>{displayOrFallback(item, ['country_code', 'country', 'countryCode'])}</td>
                               <td>{formatTimestamp(item.timestamp || item.created_at || item.date || item.time) || '-'}</td>
                             </tr> ))} </tbody>
                       </table>
                    </div>

                     {domainTotalPages > 1 && ( <div className="pagination-controls-bottom" style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                          <button onClick={handleDomainPreviousPage} disabled={domainCurrentPage <= 1 || isDomainsLoading}>{t('previousButton')}</button>
                          <span>{t('pageInfo', { currentPage: domainCurrentPage, totalPages: domainTotalPages })}</span>
                          <button onClick={handleDomainNextPage} disabled={domainCurrentPage >= domainTotalPages || isDomainsLoading}>{t('nextButton')}</button>
                        </div> )}
                  </div>
                )}
                {!isDomainsLoading && domainTotalItems === 0 && domainSearchTerm && ( <p className="info-message">{t('noDomainResultsFound', { searchTerm: domainSearchTerm })}</p> )}
                {!isDomainsLoading && domainTotalItems === 0 && !domainSearchTerm && !domainError && ( <p className="info-message">{t('noDomainDataDefault')}</p> )}
              </>
            )}
          </>
        )}
      </main>

       {/* Modal de Exportación */}
       {isExporting && <ExportModal />}
     </div>
   );
 }

export default App; 